import { apiClient } from "./client";

export interface TenantBrief {
  id: string;
  slug: string;
  display_name: string;
}

export interface AuthUser {
  id: string;
  login: string;
  display_name: string;
  email: string | null;
  role: "super_admin" | "tenant_admin" | "tenant_operator" | "tenant_readonly";
  /** Effective tenant — the one whose data is currently being served. May differ
   *  from `home_tenant_id` if super_admin has X-Tenant-Override active. */
  tenant_id: string | null;
  tenant_slug: string | null;
  /** Persistent home tenant from the users table — preserved across overrides. */
  home_tenant_id?: string | null;
  must_change_password: boolean;
  /** Effective fine-grained permissions `resource:action` (system role ∪ custom
   *  roles). Drives `auth.can()` (RBAC Block B6). */
  permissions?: string[];
  /** Only set for super_admin — list of all active tenants for the switcher. */
  available_tenants?: TenantBrief[];
}

/** Login either completes (user) or asks for a second factor (mfaRequired + token). */
export type LoginResult = { user: AuthUser } | { mfaRequired: true; mfaToken: string };

export interface MfaStatus {
  enabled: boolean;
  enrolled_at: string | null;
}

export interface MfaEnrollment {
  secret: string;
  otpauth_uri: string;
  qr_data_url: string;
}

/**
 * Public pre-login hint for a tenant (no auth required). Tells the login page
 * whether this tenant uses single sign-on and how to present it:
 *   - `local`    — only the password form (default; current behaviour).
 *   - `optional` — password form PLUS a «Войти через {Банк}» button.
 *   - `forced`   — go straight to the bank IdP (no local form).
 */
export interface SsoInfo {
  login_mode: "forced" | "optional" | "local";
  sso_enabled: boolean;
  /** Display name of the bank IdP for the button label (e.g. "Дом.РФ"). */
  idp_button_label?: string | null;
}

export const authApi = {
  async login(login: string, password: string): Promise<LoginResult> {
    const res = await apiClient.post<{ user?: AuthUser; mfa_required?: boolean; mfa_token?: string }>(
      "/v1/auth/login",
      { login, password },
    );
    if (res.data.mfa_required && res.data.mfa_token) {
      return { mfaRequired: true, mfaToken: res.data.mfa_token };
    }
    return { user: res.data.user! };
  },

  /** Finish a login that returned mfaRequired, with a TOTP or backup code. */
  async verifyMfa(mfaToken: string, code: string): Promise<AuthUser> {
    const res = await apiClient.post<{ user: AuthUser }>("/v1/auth/mfa/verify", { mfa_token: mfaToken, code });
    return res.data.user;
  },

  async me(): Promise<AuthUser> {
    const res = await apiClient.get<{ user: AuthUser }>("/v1/auth/me");
    return res.data.user;
  },

  async logout(): Promise<void> {
    await apiClient.post("/v1/auth/logout");
  },

  /** NON5 — self-service password change. */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ ok: boolean }> {
    const res = await apiClient.post<{ ok: boolean }>("/v1/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return res.data;
  },

  // ── MFA enrolment management (for the authenticated user, on the Settings page) ──
  async mfaStatus(): Promise<MfaStatus> {
    const res = await apiClient.get<MfaStatus>("/v1/auth/mfa/status");
    return res.data;
  },

  async mfaEnroll(): Promise<MfaEnrollment> {
    const res = await apiClient.post<MfaEnrollment>("/v1/auth/mfa/enroll");
    return res.data;
  },

  /** Confirm enrolment with a code from the authenticator app → returns backup codes (shown once). */
  async mfaConfirm(code: string): Promise<{ enabled: boolean; backup_codes: string[] }> {
    const res = await apiClient.post<{ enabled: boolean; backup_codes: string[] }>(
      "/v1/auth/mfa/enroll/confirm",
      { code },
    );
    return res.data;
  },

  async mfaDisable(code: string): Promise<{ enabled: boolean }> {
    const res = await apiClient.post<{ enabled: boolean }>("/v1/auth/mfa/disable", { code });
    return res.data;
  },

  // ── Single sign-on (OIDC / Keycloak) — BFF flow, no tokens in the SPA ──

  /** Public pre-login hint: does this tenant use SSO, and in which mode? */
  async ssoInfo(tenant: string): Promise<SsoInfo> {
    const res = await apiClient.get<SsoInfo>("/v1/auth/sso-info", { params: { tenant } });
    return res.data;
  },

  /**
   * Browser-facing URL that starts the OIDC Authorization-Code (+PKCE) flow.
   * This is a full-page navigation target (`window.location`), NOT an axios
   * call: the BFF 302-redirects the browser to the bank IdP and, on callback,
   * drops the httpOnly `cdp_session` cookie. The SPA never handles a token.
   */
  ssoLoginStartUrl(tenant: string, redirect?: string): string {
    const base = (apiClient.defaults.baseURL ?? "/api").replace(/\/+$/, "");
    const qs = new URLSearchParams({ tenant });
    if (redirect) qs.set("redirect", redirect);
    return `${base}/v1/sso/login-start?${qs.toString()}`;
  },
};
